import { useCallback, useEffect, useState } from "react";
import { Button } from "react-bootstrap";
import { useSelector } from "react-redux";

import { useGetGymAdminAppVersionsMutation } from "../slices/appVersionSlice";
import {
  compareVersions,
  pickLatestRelease,
  resolveStoreUrl,
  readCheck,
  writeCheck,
  todayKey,
} from "../utils/appVersion";

/**
 * Shows "a new version is available" inside the iOS/Android builds only.
 *
 * Deliberately fail-quiet: any problem (not running under Capacitor, API down,
 * unparsable version, blocked storage) results in nothing being rendered,
 * never in an error reaching the user. Renders null on the web, so the site is
 * completely unaffected.
 */
const AppUpdateBanner = () => {
  const { userInfo } = useSelector((state) => state.auth);
  const [getGymAdminAppVersions] = useGetGymAdminAppVersionsMutation();

  const [update, setUpdate] = useState(null); // { version, message, storeUrl }

  const runCheck = useCallback(async () => {
    try {
      // Capacitor is imported lazily so the plain website never evaluates it.
      const { Capacitor } = await import("@capacitor/core");
      if (!Capacitor.isNativePlatform()) return;

      const platform = Capacitor.getPlatform(); // "ios" | "android"
      if (platform !== "ios" && platform !== "android") return;

      // getInfo() throws on web, which the guard above already ruled out.
      const { App: CapacitorApp } = await import("@capacitor/app");
      const info = await CapacitorApp.getInfo();
      const currentVersion = info?.version;
      if (!currentVersion) return;

      // Compare a stored result against what is actually installed, so the
      // banner disappears by itself once the user has updated.
      const show = (result) => {
        if (!result?.latestVersion || !result?.storeUrl) return;
        if (compareVersions(result.latestVersion, currentVersion) !== 1) return;
        setUpdate({
          version: result.latestVersion,
          message: result.message,
          storeUrl: result.storeUrl,
        });
      };

      const cached = readCheck();
      if (cached?.checkedOn === todayKey()) {
        // Already asked the server today — reuse the answer instead of calling
        // again, while still showing a pending update after an app restart.
        show(cached);
        return;
      }

      let rows;
      try {
        rows = await getGymAdminAppVersions({
          platform,
          token: userInfo.token,
        }).unwrap();
      } catch (e) {
        // 404 is the API's normal "no release rows" answer, not a failure.
        // Record it, otherwise the call would repeat on every single open.
        if (e?.status === 404) {
          writeCheck({ checkedOn: todayKey() });
        }
        // Anything else (offline, 5xx, expired token) is left unrecorded so it
        // retries on the next open rather than being skipped for the day.
        return;
      }

      const latest = pickLatestRelease(rows);
      const result = {
        checkedOn: todayKey(),
        latestVersion: latest?.version || null,
        message: latest?.message || null,
        storeUrl: latest ? resolveStoreUrl(latest.downloadUrl, platform) : null,
      };
      writeCheck(result);
      show(result);
    } catch (e) {
      console.log("App version check skipped:", e?.data?.error || e?.message);
    }
  }, [getGymAdminAppVersions, userInfo?.token]);

  useEffect(() => {
    // The API sits behind isValidAssociate, so there is nothing to ask for
    // until the user is logged in.
    if (!userInfo?.token) return;

    runCheck();

    // A phone app is usually resumed rather than relaunched, so without this a
    // long-running session would never re-check after the date rolls over.
    let listener;
    let cancelled = false;
    (async () => {
      try {
        const { Capacitor } = await import("@capacitor/core");
        if (!Capacitor.isNativePlatform() || cancelled) return;
        const { App: CapacitorApp } = await import("@capacitor/app");
        listener = await CapacitorApp.addListener(
          "appStateChange",
          ({ isActive }) => {
            if (isActive) runCheck();
          }
        );
      } catch {
        // No Capacitor here; the mount-time check above is all we get.
      }
    })();

    return () => {
      cancelled = true;
      listener?.remove?.();
    };
  }, [userInfo?.token, runCheck]);

  if (!update) return null;

  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        justifyContent: "center",
        gap: "0.75rem",
        padding: "0.6rem 1rem",
        margin: "0.5rem",
        borderRadius: "0.5rem",
        backgroundColor: "#fff3cd",
        color: "#664d03",
        border: "1px solid #ffe69c",
      }}
    >
      <span style={{ fontSize: "0.9rem", fontWeight: 600 }}>
        A new version of this app is available ({update.version}).
        {update.message ? ` ${update.message}` : ""}
      </span>
      <Button
        size="sm"
        variant="warning"
        onClick={() => window.open(update.storeUrl, "_blank", "noopener")}
      >
        Update
      </Button>
    </div>
  );
};

export default AppUpdateBanner;
